<template>
  <div class="contents koyoukeiyaku">
    <h2>雇用契約 {{ title }}</h2>
    <div class="container koyoukeiyaku-search">
      <loading v-if="!isInit" :message="'初期化中...'"></loading>

      <p v-else-if="isError">初期化に失敗しました。</p>

      <template v-else-if="isInit && options && searchCondition">
        <div class="search">
          <div class="conditions">
            <div class="row">
              <div class="head"><b>社員番号</b></div>
              <input-text v-model="searchCondition.staff_code" type="text" class="medium"></input-text>
              <div class="head"><b>雇用契約ID</b></div>
              <input-text v-model="searchCondition.koyou_keiyaku_id" type="text" class="medium"></input-text>
            </div>

            <div class="row">
              <div class="head"><b>氏名</b></div>
              <input-text v-model="searchCondition.staff_name" type="text" class="medium"></input-text>
              <div class="head"><b>フリガナ</b></div>
              <input-text v-model="searchCondition.staff_kana" type="text" class="medium"></input-text>
            </div>

            <!-- TODO:各項目を未選択できる様にする -->
            <div class="row">
              <div class="facility">
                <div class="head"><b>就業場所</b></div><!-- TODO: 部課の値に応じて、オプションを絞り込む-->
                <select-box :hasBlank="true" :options="options.syuugyou_basyo"
                  v-model="searchCondition.mst_syuugyou_basyo_id" class="large"></select-box>
              </div>
              <div class="facility">
                <div class="head"><b>部課</b></div>
                <select-box :hasBlank="true" :options="options.buka" v-model="searchCondition.mst_buka_id" class="large"></select-box>
              </div>
            </div>

            <div class="row">
              <div class="head"><b>形態種別</b></div><!-- 1:正社員 2:契約社員（フルタイム） 3:契約社員（パート） 4:派遣社員（フルタイム） 5:派遣社員（パート）-->
              <input-checkbox :options="options.keitai_type" v-model="searchCondition.keitai_type"></input-checkbox>
            </div>

            <div class="row">
              <div class="head"><b>雇用形態種別</b></div><!-- 1:月給 2:月給無期 3:時給 4:パート 5:パート無期 -->
              <input-checkbox :options="options.koyou_type" v-model="searchCondition.koyou_type"></input-checkbox>
            </div>

            <div class="row" v-if="!hideShinseiType">
              <div class="head"><b>申請種別</b></div><!-- 1:新規 2:更新 3:内容変更 4:入替 -->
              <input-checkbox :options="options.shinsei_type" v-model="searchCondition.shinsei_type"></input-checkbox>
            </div>

            <div class="row" v-if="!hideSyouninStatusType">
              <div class="head"><b>社内承認</b></div><!-- 1:承認待ち 2:承認済 3:否認済（不採用） 4:差し戻し済 9:申請取消 -->
              <input-checkbox :options="options.syounin_status_type"
                v-model="searchCondition.syounin_status_type"></input-checkbox>
            </div>

            <div class="row" v-if="!hideHonninStatusType">
              <div class="head"><b>本人承認</b></div><!-- 1:本人未通知 2:本人通知済み 3:本人了承済み 4:本人辞退 -->
              <input-checkbox :options="options.honnin_status_type"
                v-model="searchCondition.honnin_status_type"></input-checkbox>
            </div>

            <div class="row">
              <div class="head"><b>削除済み</b></div>
              <input-checkbox :options="[{ val: 1, label: '表示する' } as BaseOptionWithoutCode]"
                v-model="searchCondition.includeSakujo"></input-checkbox>
            </div>

            <div class="buttons">
              <button class="btn cancel" v-on:click="clear()">条件のリセット</button>
              <button class="btn primary" v-on:click="search()">検索</button>
            </div>
          </div>
          <component :is="List" :KoyouKeiyakuList="KoyouKeiyakuList" :isSearching="isSearching" />
        </div><!-- search -->

      </template>
    </div><!--container-->

    <div class="button-container">
      <p class="prev-page">
        <router-link :to="{
          name: RouterConst.KOYOUKEIYAKU_MENU,
        }">メニューに戻る
        </router-link>
      </p>
    </div><!--button-container-->

  </div><!--contents-->
</template>

<script lang="ts">

import { computed, defineComponent, onMounted, ref } from 'vue'
import { useKoyouKeiyakuInit } from '@/composable/koyoukeiyaku/init';
import { useKoyouKeiyakuSearch } from '@/composable/koyoukeiyaku/search';
import List from '@/components/koyoukeiyaku/List.vue';
import { RouterConst } from '@/const/Router';
import OptionConst from '@/const/Option';
import { useKoyouKeiyakuSearchStore } from '@/store/koyoukeiyaku-search';
import { useAuthStore } from '@/store/auth';
import { cloneDeep } from 'lodash';
import { storeToRefs } from 'pinia';
import { BaseOptionWithoutCode } from '@/types/Option';

export default defineComponent({
  setup() {

    // 初期化
    const { isLoading, isError, searchCondition, options, getAllKoyouKeiyakuOption } = useKoyouKeiyakuInit();

    // ログインユーザーID取得
    const authStore = useAuthStore();
    const loginUserId = authStore.user?.user_id;

    // 雇用契約ストア
    const searchStore = useKoyouKeiyakuSearchStore();

    const { getKoyouKeiyakuList, KoyouKeiyakuList, isSearching } = useKoyouKeiyakuSearch();

    const isInit = ref(false)
    onMounted(async () => {
      await getAllKoyouKeiyakuOption()
      getKoyouKeiyakuList();
      isInit.value = true
    })

    // 検索ボタンクリック
    const search = async () => {
      // ストアデータを更新
      await getKoyouKeiyakuList();
    }

    // 入力リセットボタン
    const clear = async () => {
      await searchStore.reset();
      isSearching.value = false;
    }

    const title = computed(() =>{
      switch (searchCondition.value.searchType) {
        case OptionConst.KEIYAKU_SEARCH_TYPE.FREE:
          return '自由検索'
        case OptionConst.KEIYAKU_SEARCH_TYPE.KOUSHIN:
          return '更新前一覧'
        case OptionConst.KEIYAKU_SEARCH_TYPE.NYUUSYA:
          return '入社スタッフ更新前一覧';
        case OptionConst.KEIYAKU_SEARCH_TYPE.SYANAI_SYOUNIN:
          return '社内承認中一覧';
        case OptionConst.KEIYAKU_SEARCH_TYPE.HONNIN_SYOUNIN:
          return '本人承認中一覧';
        default:
          return '検索';
      }
    })

    // 申請種別の表示制御
    const hideShinseiType = computed(() => {
      switch (searchCondition.value.searchType) {
        case OptionConst.KEIYAKU_SEARCH_TYPE.SYANAI_SYOUNIN:
        case OptionConst.KEIYAKU_SEARCH_TYPE.KOUSHIN:
        case OptionConst.KEIYAKU_SEARCH_TYPE.NYUUSYA:
          searchCondition.value.shinsei_type = []
          return true
        default:
          return false
      }
    })

    // 社内承認種別の表示制御
    const hideSyouninStatusType = computed(() => {
      if (!searchCondition) {
        return true
      }
      switch (searchCondition.value.searchType) {
        case OptionConst.KEIYAKU_SEARCH_TYPE.HONNIN_SYOUNIN:
        case OptionConst.KEIYAKU_SEARCH_TYPE.EDIT:
        case OptionConst.KEIYAKU_SEARCH_TYPE.KOUSHIN:
        case OptionConst.KEIYAKU_SEARCH_TYPE.NYUUSYA:
          searchCondition.value.syounin_status_type = []
          return true
        default:
          return false
      }
    })

    // 本人ステータス種別の表示制御
    const hideHonninStatusType = computed(() => {
      if (!searchCondition) {
        return true
      }
      switch (searchCondition.value.searchType) {
        case OptionConst.KEIYAKU_SEARCH_TYPE.SYANAI_SYOUNIN:
        case OptionConst.KEIYAKU_SEARCH_TYPE.EDIT:
        case OptionConst.KEIYAKU_SEARCH_TYPE.KOUSHIN:
        case OptionConst.KEIYAKU_SEARCH_TYPE.NYUUSYA:
          searchCondition.value.honnin_status_type = []
          return true
        default:
          return false
      }
    })

    return {
      isError,
      searchCondition,
      search,
      List,
      options,
      RouterConst,
      clear,
      isSearching,
      isInit,
      KoyouKeiyakuList,
      OptionConst,
      hideSyouninStatusType,
      hideHonninStatusType,
      hideShinseiType,
      title
    }
  },
})

</script>


<style lang="scss" scoped>
@import '@scss/components/koyoukeiyaku.scss';
@import '@scss/components/koyoukeiyaku-search.scss';
</style>
